import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Avatar, Box, Collapse, Portal } from "@mui/material";
import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { isMobile } from "react-device-detect";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LogoutIcon from "@mui/icons-material/Logout";
import useTheme from "../../../../common/hooks/useTheme";
import Logo from "../../../../common/svgs/logo";
import { useAuth } from "../../../../providers/AuthProvider";
import { appendChatModeToPath } from "../../../chat/chatMode";
import {
  NavItem,
  affiliateNavigation,
  clientWorkspaceNavigation,
  customerNavigation,
} from "./navigationStructure";
import { SIDEBAR_WIDTHS } from "./SidebarContext";
import ScrollableWrapper from "../common/ScrollableWrapper";
import { Theme } from "../../../../providers/ThemeProvider";

const FLYOUT_CONFIG = {
  LEVEL_1_OFFSET: 44,
  LEVEL_2_OFFSET: 268,
  HEADER_HEIGHT: 64,
  ITEM_HEIGHT: 41,
  PADDING_TOP: 8,
} as const;

const INDENTATION = {
  LEVEL_0: "6px",
  LEVEL_1: "26px",
  LEVEL_2: "50px",
} as const;

/**
 * Which shell is this sidebar rendered in?
 * - affiliate: the main tab (client list, orders, services, walkthroughs)
 * - client: a client workspace tab — client pages with `?id=` stamped from the URL
 * - self: the customer's own workspace (/platform routes)
 */
export type SidebarVariant = "affiliate" | "client" | "self";

const VARIANT_NAV_ITEMS: Record<SidebarVariant, NavItem[]> = {
  affiliate: affiliateNavigation,
  client: clientWorkspaceNavigation,
  self: customerNavigation,
};

// Per-variant palette: affiliate main tab and the customer's own view keep
// the dark primary sidebar; the client workspace uses the light style from
// the Figma "CLIENT VIEW" sidebar (all values are exact theme-token matches).
type SidebarPalette = {
  bg: string;
  divider: string;
  icon: string;
  iconActive: string;
  text: string;
  itemHoverBg: string;
  itemHoverText: string;
  itemActiveBg: string;
  itemActiveBorder: string;
  childActiveBg: string;
  childActiveText: string;
  childActiveBorder: string;
  logo: string;
  avatarBg: string;
  avatarText: string;
  subtitle: string;
  logoutBg: string;
  logoutHoverBg: string;
  logoutText: string;
};

const getPalette = (theme: Theme, variant: SidebarVariant): SidebarPalette =>
  variant === "client"
    ? {
        bg: theme.colorTokens.P[10],
        divider: theme.colorTokens.N[50],
        icon: theme.colorTokens.N[80],
        iconActive: theme.colorTokens.P[80],
        text: theme.colorTokens.N[90],
        itemHoverBg: theme.colorTokens.P[20],
        itemHoverText: theme.colorTokens.N[90],
        itemActiveBg: theme.colorTokens.P[20],
        itemActiveBorder: theme.colorTokens.P[80],
        childActiveBg: theme.colorTokens.P[20],
        childActiveText: theme.colorTokens.N[90],
        childActiveBorder: theme.colorTokens.P[80],
        logo: theme.colorTokens.N[90],
        avatarBg: theme.colorTokens.P[20],
        avatarText: theme.colorTokens.P[80],
        subtitle: theme.colorTokens.N[80],
        logoutBg: theme.colorTokens.P[20],
        logoutHoverBg: theme.colorTokens.P[30],
        logoutText: theme.colorTokens.P[100],
      }
    : {
        bg: theme.colorTokens.P[100],
        divider: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
        icon: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
        iconActive: theme.colorTokens.P[50],
        text: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
        itemHoverBg: theme.colorTokens.P[90],
        itemHoverText: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
        itemActiveBg: theme.colorTokens.P[90],
        itemActiveBorder: theme.colorTokens.P[50],
        childActiveBg: theme.colorTokens.P[10],
        childActiveText: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
        childActiveBorder: theme.colorTokens.P[80],
        logo: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
        avatarBg: theme.colors_ds.PRIMARY_ICON_MAIN,
        avatarText: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
        subtitle: theme.colorTokens.P[30],
        logoutBg: theme.colorTokens.P[10],
        logoutHoverBg: theme.colorTokens.P[20],
        logoutText: theme.colorTokens.P[100],
      };

const PlatformSidebar = ({ variant }: { variant: SidebarVariant }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { AuthUser, AuthIsSimpleUser, AuthLogout } = useAuth();

  // The layout never reacts to the sidebar: it always reserves the collapsed
  // width and the sidebar expands ABOVE the content on hover (desktop only).
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  // Mobile-only flyout menus (click-driven)
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoveredNested, setHoveredNested] = useState<string | null>(null);
  // Bottom workspace row: the chevron reveals the Log out button inline
  const [showLogout, setShowLogout] = useState(false);

  const navItems = VARIANT_NAV_ITEMS[variant];
  const palette = useMemo(() => getPalette(theme, variant), [theme, variant]);

  // In the client workspace, links carry the client id from the URL — the
  // URL is the source of truth for which client this tab shows.
  const urlClientId = variant === "client" ? searchParams.get("id") : null;

  const showCollapsedRail = isMobile || !isExpanded;

  // Auto-expand the section that contains the current route
  useEffect(() => {
    const routeSections: { prefix: string; id: string }[] = [
      { prefix: "/client/services", id: "services" },
      { prefix: "/client/client-info", id: "client-info" },
      { prefix: "/client/programming", id: "programming" },
      { prefix: "/platform/programming", id: "customer-programming" },
    ];
    const match = routeSections.find(({ prefix }) =>
      location.pathname.startsWith(prefix)
    );
    if (match) {
      setExpandedSections((prev) => {
        const newSections = new Set(prev);
        newSections.add(match.id);
        return Array.from(newSections);
      });
    }
  }, [location.pathname]);

  // Helper to close all mobile flyout menus
  const closeFlyouts = useCallback(() => {
    setHoveredItem(null);
    setHoveredNested(null);
  }, []);

  // Close mobile menus whenever the route changes (browser back, in-page
  // links), not only on taps the sidebar itself handles.
  useEffect(() => {
    closeFlyouts();
    setShowLogout(false);
  }, [location.pathname, closeFlyouts]);

  // Mobile: a tap anywhere outside the rail and its portalled flyouts closes
  // them (the desktop rail closes on mouse leave, which touch never fires).
  const containerRef = React.useRef<HTMLDivElement>(null);
  const anyMobileMenuOpen = isMobile && (hoveredItem !== null || showLogout);
  useEffect(() => {
    if (!anyMobileMenuOpen) return;
    const onPointerDown = (event: Event) => {
      const target = event.target as Element | null;
      if (!target) return;
      if (containerRef.current?.contains(target)) return;
      if (target.closest(".flyout-menu")) return;
      closeFlyouts();
      setShowLogout(false);
    };
    document.addEventListener("touchstart", onPointerDown, true);
    document.addEventListener("mousedown", onPointerDown, true);
    return () => {
      document.removeEventListener("touchstart", onPointerDown, true);
      document.removeEventListener("mousedown", onPointerDown, true);
    };
  }, [anyMobileMenuOpen, closeFlyouts]);

  const toggleSection = useCallback(
    (sectionId: string, isAutoExpand: boolean = false) => {
      // Don't allow manual toggle for auto-expand items
      if (isAutoExpand) return;

      setExpandedSections((prev) =>
        prev.includes(sectionId)
          ? prev.filter((id) => id !== sectionId)
          : [...prev, sectionId]
      );
    },
    []
  );

  // Helper function to build links with preserved id parameter
  const buildLink = useCallback(
    (link?: string): string | undefined => {
      if (!link) return undefined;

      // Only client workspace links carry a client id; affiliate and
      // customer (/platform) links are client-agnostic.
      if (variant !== "client" || !urlClientId) {
        return appendChatModeToPath(link, location.search);
      }

      // Parse the link to add the id parameter from the URL
      const [path, queryString] = link.split("?");
      const params = new URLSearchParams(queryString || "");
      params.set("id", urlClientId);

      return appendChatModeToPath(
        `${path}?${params.toString()}`,
        location.search
      );
    },
    [variant, urlClientId, location.search]
  );

  // Helper to check if item or its children are active
  const isActiveItem = useCallback(
    (item: NavItem, parentId?: string): boolean => {
      // Special handling for services children - check exact path match for nested routes
      if (parentId === "services") {
        const currentPath = location.pathname;

        if (item.id === "service-builder") {
          return currentPath === "/client/services";
        }
        if (item.id === "logic-builder") {
          return currentPath.includes("/services/logic");
        }
        if (item.id === "lab-builder") {
          return currentPath.includes("/services/lab");
        }
      }

      // For parent items with children, check if any child is active FIRST
      // This ensures parents are highlighted when any of their children are active
      if (item.children) {
        return item.children.some((child) => isActiveItem(child, item.id));
      }

      // For leaf items with links, check if current path starts with the item's link
      if (item.link) {
        const itemPath = item.link.split("?")[0];
        return location.pathname.startsWith(itemPath);
      }

      return false;
    },
    [location.pathname]
  );

  const renderNavItems = (
    items: NavItem[],
    isCollapsedView: boolean,
    level: number = 0,
    parentId?: string
  ) => {
    return items
      .filter((item) => !item.hideInSidebar) // Filter out hidden items
      .map((item, index) => {
        const Icon = item.icon;
        const isActive = isActiveItem(item, parentId);
        const hasChildren = Boolean(item.children);

        if (isCollapsedView && level === 0) {
          // Handler for icon click
          const handleIconClick = () => {
            if (isMobile && hasChildren) {
              // On mobile, toggle flyout on click for all items with children;
              // only one menu is open at a time, so the account menu closes.
              setShowLogout(false);
              setHoveredNested(null);
              setHoveredItem(hoveredItem === item.id ? null : item.id);
              return;
            }

            // Any other rail tap is a navigation: close an open flyout or
            // account menu. The tap-outside listener ignores taps inside the
            // rail, so it never catches these.
            closeFlyouts();
            setShowLogout(false);

            if (!hasChildren && item.link) {
              // Navigate if it's a leaf item
              navigate(buildLink(item.link) || item.link);
            } else if (item.link) {
              // Desktop parent items navigate to their landing page (the
              // expanded overlay on hover exposes the children)
              navigate(buildLink(item.link) || item.link);
            }
          };

          return (
            <Box key={item.id} sx={{ mb: 1, position: "relative" }}>
              <Box
                className="nav-icon"
                onClick={handleIconClick}
                sx={{
                  width: SIDEBAR_WIDTHS.COLLAPSED,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: palette.text,
                  cursor: "pointer",
                  background: isActive ? palette.itemActiveBg : "transparent",
                  borderLeft: `4px solid ${
                    isActive ? palette.itemActiveBorder : "transparent"
                  }`,
                  pr: "4px",
                  textDecoration: "none",
                  // Only on devices that hover: iOS can treat a tap on an
                  // element with a :hover style change as the hover itself and
                  // swallow the first click.
                  "@media (hover: hover)": {
                    "&:hover": { backgroundColor: palette.itemHoverBg },
                  },
                  transition: "all 0.2s",
                }}
              >
                {Icon && (
                  <Icon
                    sx={{
                      fontSize: 18,
                      color: isActive ? palette.iconActive : palette.icon,
                    }}
                  />
                )}
              </Box>

              {/* Flyout Menu — mobile only (no hover on touch devices; the
                  desktop affordance is the hover-expanded sidebar). Rendered
                  through a Portal: it is position:fixed, but iOS Safari clips
                  fixed children of the nav's touch-scrolling container, so
                  inside the rail it existed in the DOM yet never showed. */}
              {isMobile && hoveredItem === item.id && hasChildren && (
                <Portal>
                <Box
                  className="flyout-menu"
                  sx={{
                    position: "fixed",
                    left: `${FLYOUT_CONFIG.LEVEL_1_OFFSET}px`,
                    top: `${
                      FLYOUT_CONFIG.HEADER_HEIGHT +
                      index * FLYOUT_CONFIG.ITEM_HEIGHT +
                      FLYOUT_CONFIG.PADDING_TOP
                    }px`,
                    minWidth: "220px",
                    background: palette.bg,
                    borderRadius: "8px",
                    boxShadow: "var(--box-shadow-general)",
                    py: 1,
                    zIndex: 9999,
                    animation: "flyout 150ms ease-out",
                    "@keyframes flyout": {
                      from: {
                        opacity: 0,
                        transform: "translateX(-8px)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translateX(0)",
                      },
                    },
                  }}
                >
                  {item.children
                    ?.filter((child) => !child.hideInSidebar)
                    .map((child, childIndex) => {
                      const isChildActive = isActiveItem(child, item.id);

                      const handleChildClick = () => {
                        if (child.children) {
                          // Toggle nested flyout
                          setHoveredNested(
                            hoveredNested === child.id ? null : child.id
                          );
                        } else {
                          // Close all flyouts when navigating
                          closeFlyouts();
                        }
                      };

                      return (
                        <Box
                          key={child.id}
                          component={
                            child.children
                              ? "div"
                              : child.link
                              ? NavLink
                              : "div"
                          }
                          to={
                            child.children ? undefined : buildLink(child.link)
                          }
                          onClick={handleChildClick}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            py: 1,
                            mb:
                              childIndex < item.children!.length - 1 ? 0.5 : 0,
                            color: palette.text,
                            fontSize: "12px",
                            fontFamily: "var(--font-Medium)",
                            cursor: "pointer",
                            textDecoration: "none",
                            background: isChildActive
                              ? palette.itemActiveBg
                              : "transparent",
                            borderLeft: `4px solid ${
                              isChildActive
                                ? palette.itemActiveBorder
                                : "transparent"
                            }`,
                            "&:hover": {
                              backgroundColor: palette.itemHoverBg,
                              color: palette.itemHoverText,
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          {child.label}
                          {child.children && (
                            <ChevronRightIcon
                              sx={{ ml: "auto", fontSize: 16 }}
                            />
                          )}

                          {/* Second Level Flyout */}
                          {hoveredNested === child.id && child.children && (
                            <Box
                              className="flyout-level-2"
                              sx={{
                                position: "fixed",
                                left: `${FLYOUT_CONFIG.LEVEL_2_OFFSET}px`,
                                top: `${
                                  FLYOUT_CONFIG.HEADER_HEIGHT +
                                  index * FLYOUT_CONFIG.ITEM_HEIGHT +
                                  FLYOUT_CONFIG.PADDING_TOP +
                                  FLYOUT_CONFIG.PADDING_TOP +
                                  item.children!.indexOf(child) * 40
                                }px`,
                                minWidth: "200px",
                                background: palette.bg,
                                borderRadius: "8px",
                                boxShadow: "var(--box-shadow-general)",
                                py: 1,
                                zIndex: 10000,
                                animation: "flyout 150ms ease-out",
                              }}
                            >
                              {child.children.map(
                                (grandChild, grandChildIndex) => {
                                  const isGrandChildActive = isActiveItem(
                                    grandChild,
                                    child.id
                                  );

                                  return (
                                    <Box
                                      key={grandChild.id}
                                      component={
                                        grandChild.link ? NavLink : "div"
                                      }
                                      to={buildLink(grandChild.link)}
                                      onClick={closeFlyouts}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        px: 2,
                                        py: 1,
                                        mb:
                                          grandChildIndex <
                                          child.children!.length - 1
                                            ? 0.5
                                            : 0,
                                        color: isGrandChildActive
                                          ? palette.childActiveText
                                          : palette.text,
                                        fontSize: "12px",
                                        fontFamily: "var(--font-Medium)",
                                        cursor: "pointer",
                                        textDecoration: "none",
                                        background: isGrandChildActive
                                          ? palette.childActiveBg
                                          : "transparent",
                                        borderLeft: `6px solid ${
                                          isGrandChildActive
                                            ? palette.childActiveBorder
                                            : "transparent"
                                        }`,
                                        "&:hover": {
                                          backgroundColor:
                                            palette.itemHoverBg,
                                          color: palette.itemHoverText,
                                        },
                                        transition: "all 0.2s",
                                      }}
                                    >
                                      {grandChild.label}
                                    </Box>
                                  );
                                }
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                </Box>
                </Portal>
              )}
            </Box>
          );
        }

        // Handler for parent items (with children)
        const handleParentClick = () => {
          // Allow manual toggle for all parent items
          toggleSection(item.id);
        };

        // Single render shape for the desktop sidebar: the collapsed rail is
        // just this same row clipped to the rail width (labels fade, chevrons
        // collapse), so the hover expansion is one smooth width transition
        // with no DOM swap.
        return (
          <Box key={item.id} sx={{ mb: 1 }}>
            <Box
              component={hasChildren ? "div" : NavLink}
              to={hasChildren ? undefined : buildLink(item.link)}
              onClick={hasChildren ? handleParentClick : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                pl:
                  level === 0
                    ? INDENTATION.LEVEL_0
                    : level === 1
                    ? INDENTATION.LEVEL_1
                    : INDENTATION.LEVEL_2,
                pr: 1,
                py: 1,
                cursor: "pointer",
                textDecoration: "none",
                background:
                  level === 2
                    ? isActive
                      ? palette.childActiveBg
                      : "transparent"
                    : isActive
                    ? palette.itemActiveBg
                    : "transparent",
                color:
                  level === 2 && isActive ? palette.childActiveText : palette.text,
                fontFamily: "var(--font-Medium)",
                fontSize: "12px",
                borderLeft: isActive
                  ? level === 2
                    ? `6px solid ${palette.childActiveBorder}`
                    : `6px solid ${palette.itemActiveBorder}`
                  : "6px solid transparent",
                "&:hover": {
                  backgroundColor:
                    level === 2 ? palette.childActiveBg : palette.itemHoverBg,
                  color:
                    level === 2
                      ? palette.childActiveText
                      : palette.itemHoverText,
                },
                transition: "all 0.2s",
              }}
            >
              {hasChildren && (
                <Box
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    toggleSection(item.id, item.autoExpand);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    overflow: "hidden",
                    // The chevron collapses away on the rail so the icon sits
                    // at the rail position; it slides in on hover expansion.
                    width: showCollapsedRail && level === 0 ? 0 : "24px",
                    opacity: showCollapsedRail && level === 0 ? 0 : 1,
                    transition: "width 0.3s ease-in-out, opacity 0.2s",
                    flexShrink: 0,
                  }}
                >
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 16,
                      transform: expandedSections.includes(item.id)
                        ? ""
                        : "rotate(-90deg)",
                    }}
                  />
                </Box>
              )}
              {Icon && (
                <Icon
                  sx={{
                    fontSize: 18,
                    flexShrink: 0,
                    color: isActive ? palette.iconActive : palette.icon,
                  }}
                />
              )}
              <Box
                sx={{
                  flexGrow: 1,
                  ml: Icon || hasChildren ? "8px" : 0,
                  whiteSpace: "nowrap",
                  opacity: showCollapsedRail ? 0 : 1,
                  transition: "opacity 0.25s",
                }}
              >
                {item.label}
              </Box>
            </Box>

            {hasChildren && (
              <Collapse
                in={
                  expandedSections.includes(item.id) &&
                  (!showCollapsedRail || level > 0)
                }
                timeout={300}
              >
                <Box sx={{ mt: 1 }}>
                  {renderNavItems(item.children!, false, level + 1, item.id)}
                </Box>
              </Collapse>
            )}
          </Box>
        );
      });
  };

  // Bottom account section: the logged-in affiliate's avatar with the company
  // name and email. The customer's own view keeps the avatar in the top bar
  // instead — and stays narrow, since it has no account row to fit.
  const showBottomAvatar = variant !== "self" && !AuthIsSimpleUser;
  const affiliateInitial = AuthUser.name?.charAt(0)?.toUpperCase() || "";

  // Memoize styles to prevent recreation on every render (prevents flickering)
  const styles = useMemo(
    () => getStyles(palette, showCollapsedRail, showBottomAvatar),
    [palette, showCollapsedRail, showBottomAvatar]
  );

  const sidebarContent = (
    <Box sx={styles.sidebarContent}>
      {/* Header — the full wordmark, clipped to the Ᵽ mark on the collapsed
          rail. One svg in both states so the P never resizes or jumps; the
          letters are revealed by the same width transition as the rail. */}
      <Box sx={styles.header}>
        {isMobile ? (
          <Logo markOnly width={17} height={15} color={palette.logo} />
        ) : (
          <Box
            sx={{
              width: showCollapsedRail ? "17px" : "60px",
              overflow: "hidden",
              flexShrink: 0,
              transition: "width 0.3s ease-in-out",
            }}
          >
            <Logo width={60} height={15} color={palette.logo} />
          </Box>
        )}
      </Box>

      {/* Navigation Items — mobile keeps the static icon rail with click
          flyouts; desktop always renders the full rows and lets the width
          transition clip them (no remount, no flash) */}
      {/* reserveGutter={false}: the nav fits without scrolling at almost every
          viewport height, and a permanently reserved 8px gutter reads as a gap
          on the right edge of the sidebar. */}
      <ScrollableWrapper isDark={variant !== "client"} reserveGutter={false}>
        <Box sx={styles.navigationContainer}>
          {renderNavItems(navItems, isMobile, 0)}
        </Box>
      </ScrollableWrapper>

      {/* Bottom account section (affiliate variants). Per design there is no
          popup: the chevron reveals a Log out button that sits ABOVE the
          divider, with the account row below it; Account settings lives in
          the nav above. */}
      {showBottomAvatar && (
        <>
          <Collapse in={showLogout && !showCollapsedRail} timeout={200}>
            <Box sx={styles.logoutWrapper}>
              <Box sx={styles.logoutButton} onClick={() => AuthLogout()}>
                <LogoutIcon sx={{ fontSize: 12, flexShrink: 0 }} />
                <Box sx={styles.logoutLabel}>Log out</Box>
              </Box>
            </Box>
          </Collapse>

          <Box sx={styles.bottomSection}>
            <Box
              sx={styles.bottomAvatarRow}
              onClick={() => {
                // One menu at a time: the account menu replaces any nav flyout.
                closeFlyouts();
                setShowLogout((prev) => !prev);
              }}
            >
              <Avatar sx={styles.bottomAvatar}>{affiliateInitial}</Avatar>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexGrow: 1,
                  minWidth: 0,
                  gap: "8px",
                  opacity: showCollapsedRail ? 0 : 1,
                  transition: "opacity 0.25s",
                }}
              >
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Box sx={styles.bottomAvatarName}>{AuthUser.name || ""}</Box>
                  <Box sx={styles.bottomAvatarSubtitle}>
                    {AuthUser.email || ""}
                  </Box>
                </Box>
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 16,
                    flexShrink: 0,
                    color: palette.icon,
                    transform: showLogout ? "rotate(0deg)" : "rotate(180deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Mobile: the rail never expands, so the inline Log out reveal above
              can never open. Show the account + Log out in a portalled flyout
              beside the avatar instead (same reasoning as the nav flyouts). */}
          {isMobile && showLogout && (
            <Portal>
              <Box
                className="flyout-menu"
                sx={styles.mobileAccountMenu}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <Box sx={{ px: "12px", pb: "8px", minWidth: 0 }}>
                  <Box sx={styles.bottomAvatarName}>{AuthUser.name || ""}</Box>
                  <Box sx={styles.bottomAvatarSubtitle}>
                    {AuthUser.email || ""}
                  </Box>
                </Box>
                <Box sx={styles.logoutButton} onClick={() => AuthLogout()}>
                  <LogoutIcon sx={{ fontSize: 12, flexShrink: 0 }} />
                  <Box sx={styles.logoutLabel}>Log out</Box>
                </Box>
              </Box>
            </Portal>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      sx={styles.container}
      onMouseEnter={!isMobile ? () => setIsExpanded(true) : undefined}
      onMouseLeave={
        !isMobile
          ? () => {
              setIsExpanded(false);
              // Collapse the Log out reveal with the sidebar
              setShowLogout(false);
            }
          : undefined
      }
    >
      {sidebarContent}
    </Box>
  );
};

// Styles object - extracted for main container and header
// Note: Navigation item styles remain inline due to their dynamic nature
// (they depend on multiple parameters: isActive, level, index, childIndex, etc.)
const getStyles = (
  palette: SidebarPalette,
  isCollapsed: boolean,
  hasAccountRow: boolean
) => ({
  container: {
    width: isCollapsed
      ? `${SIDEBAR_WIDTHS.COLLAPSED}px`
      : `${
          hasAccountRow
            ? SIDEBAR_WIDTHS.EXPANDED_WITH_ACCOUNT
            : SIDEBAR_WIDTHS.EXPANDED
        }px`,
    // dvh follows iOS Safari's collapsing toolbar; 100vh is the tallest
    // viewport there, which hid the avatar row behind the toolbar.
    height: "100vh",
    "@supports (height: 100dvh)": { height: "100dvh" },
    position: "fixed" as const,
    left: 0,
    top: 0,
    // Above the sticky top bar (zIndex 100): the expanded sidebar overlays
    // the content instead of pushing it.
    zIndex: 1200,
    transition: "width 0.3s ease-in-out",
    overflow: "hidden",
    willChange: "width",
    boxShadow: isCollapsed ? "none" : "var(--box-shadow-general)",
  },
  sidebarContent: {
    display: "flex",
    flexDirection: "column" as const,
    height: "100%",
    bgcolor: palette.bg,
    transition: "all 0.3s",
  },
  header: {
    display: "flex",
    alignItems: "center",
    // Constant left padding keeps the Ᵽ mark at the exact same spot in both
    // states — only the clip width animates.
    justifyContent: "flex-start",
    minHeight: "48px",
    py: 0.5,
    pl: "11px",
    borderBottom: `1px solid ${palette.divider}`,
  },
  navigationContainer: {
    mt: 2,
    whiteSpace: "nowrap" as const,
  },
  bottomSection: {
    borderTop: `1px solid ${palette.divider}`,
    py: "12px",
    // Constant padding: avatar (31px) stays centered on the 40px rail and
    // simply gains the name to its right on expansion.
    px: "2px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "stretch",
  },
  // The Log out reveal lives above the divider (see design), so it carries
  // its own horizontal padding instead of inheriting the account section's.
  logoutWrapper: {
    px: "8px",
    pb: "8px",
  },
  mobileAccountMenu: {
    position: "fixed" as const,
    left: `${FLYOUT_CONFIG.LEVEL_1_OFFSET}px`,
    bottom: "12px",
    minWidth: "220px",
    maxWidth: "calc(100vw - 56px)",
    background: palette.bg,
    borderRadius: "8px",
    boxShadow: "var(--box-shadow-general)",
    py: 1,
    px: "8px",
    zIndex: 9999,
  },
  bottomAvatarRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
    cursor: "pointer",
    borderRadius: "8px",
    px: "2px",
    py: "4px",
    // See the nav icon: hover styling only where hovering exists.
    "@media (hover: hover)": {
      "&:hover": { backgroundColor: palette.itemHoverBg },
    },
    transition: "all 0.2s",
  },
  bottomAvatar: {
    width: 31,
    height: 31,
    backgroundColor: palette.avatarBg,
    color: palette.avatarText,
    fontSize: "16px",
    fontFamily: "var(--font-Bold)",
  },
  bottomAvatarName: {
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: palette.text,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  bottomAvatarSubtitle: {
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "14px",
    color: palette.subtitle,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  logoutButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    px: "8px",
    py: "10px",
    borderRadius: "8px",
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
    backgroundColor: palette.logoutBg,
    color: palette.logoutText,
    "&:hover": {
      backgroundColor: palette.logoutHoverBg,
    },
    transition: "background-color 0.2s",
  },
  logoutLabel: {
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
  },
});

export default PlatformSidebar;
